import React, { useCallback, useEffect, useState } from 'react';
import { BookOpen, FileText, Plus, Pencil, Trash2, ArrowUpToLine, Save, X, Search } from 'lucide-react';
import IntestazioneSezione from '../components/comuni/IntestazioneSezione';
import EditorReferto from '../components/referti/EditorReferto';
import { testiAPI } from '../services/testiApi';
import { useAuthStore } from '../store/useStore';
import { useSporco, useUscitaProtetta } from '../utils/uscitaProtetta';
import useAzione from '../navigazione/useAzione';
import '../styles/TestiRiutilizzabili.css';

/**
 * TestiRiutilizzabili — la libreria, in una schermata sola.
 *
 * Frasi da inserire in un referto e protocolli da consegnare al
 * paziente sono la stessa cosa con destinazioni diverse: un testo con
 * un titolo. Qui si vedono, si scrivono e - per l'amministrazione - si
 * promuovono da «mio» a «della struttura». Due portate, come le schede:
 * il medico compone dai condivisi e si fa i suoi; la promozione e' un
 * atto dell'amministrazione, non del medico.
 *
 * Chi vede cosa lo decide il server: un medico riceve i condivisi e i
 * suoi, l'amministrazione tutto, la segreteria i condivisi.
 *
 * «RICETTE» E' QUESTA STESSA SCHERMATA (Alex, 28/09/2026), con
 * `soloTipo="ricetta"`: lo stesso catalogo dei testi di tipo «ricetta», non
 * una seconda libreria. Si cerca per titolo e testo, si aggiunge, si
 * modifica e si RITIRA (spenta, non cancellata: attivo = false). Una ricetta
 * gia' usata in una visita non cambia: nella ricetta della visita c'e' una
 * copia del testo, messa li' al momento del clic (LibreriaRicette).
 */

const TIPI = [
    { chiave: 'frase', etichetta: 'Frase', spiegazione: 'da inserire in un referto o in una ricetta' },
    { chiave: 'protocollo', etichetta: 'Protocollo', spiegazione: 'da consegnare al paziente, per esempio dopo un trattamento' },
    /* Il catalogo delle ricette (proposta 17, 4c): nell'editor di una
       ricetta compaiono sopra il titolo, e un clic le aggiunge in fondo. */
    { chiave: 'ricetta', etichetta: 'Ricetta', spiegazione: 'compare sopra il titolo di una ricetta: un clic la aggiunge in fondo al foglio' },
    { chiave: 'altro', etichetta: 'Altro', spiegazione: '' },
];
const etichettaTipo = (t) => TIPI.find((x) => x.chiave === t)?.etichetta || t;

const VUOTO = { titolo: '', tipo: 'frase', contenuto: { type: 'doc', content: [{ type: 'paragraph' }] } };

const messaggio = (e, ripiego) => e?.response?.data?.message || e?.message || ripiego;

/* Il modulo di scrittura: uno solo per creare e modificare. */
const ModuloTesto = ({ iniziale, onSalvato, onChiudi, tipoFisso = null }) => {
    const [dati, setDati] = useState(iniziale || VUOTO);
    const [inCorso, setInCorso] = useState(false);
    const [errore, setErrore] = useState(null);
    const [versione, setVersione] = useState(0);
    const sporco = useSporco({ titolo: dati.titolo, tipo: dati.tipo, contenuto: { ...dati.contenuto, __id: undefined } }, versione);
    const chiedi = useUscitaProtetta(sporco);

    useEffect(() => { setDati(iniziale || VUOTO); setVersione((v) => v + 1); }, [iniziale]);

    const salva = async () => {
        setInCorso(true);
        setErrore(null);
        try {
            const { __id, ...contenuto } = dati.contenuto || {};
            const corpo = { titolo: dati.titolo, tipo: dati.tipo, contenuto };
            const r = dati.id ? await testiAPI.aggiorna(dati.id, corpo) : await testiAPI.crea(corpo);
            setVersione((v) => v + 1);
            onSalvato(r.data?.data?.testo, r.data?.message);
        } catch (e) {
            setErrore(messaggio(e, 'Errore nel salvataggio'));
        } finally {
            setInCorso(false);
        }
    };

    /* UN TESTO GIA' SCRITTO SI SALVA DA SOLO uscendo dai campi (regola di
       Alex del 24/09/2026): niente «Salva», si chiude con «Chiudi». Un
       testo NUOVO resta un gesto, «Crea il testo», perche' crea una riga.
       Se niente e' cambiato non parte niente; se non riesce il testo resta
       e si riprova. */
    const [statoSalva, setStatoSalva] = useState(null);
    const [salvatoQualcosa, setSalvatoQualcosa] = useState(false);
    const salvaEsistente = async () => {
        if (!dati.id || !sporco || inCorso || !dati.titolo.trim()) return;
        setInCorso(true);
        setErrore(null);
        setStatoSalva('in_corso');
        try {
            const { __id, ...contenuto } = dati.contenuto || {};
            await testiAPI.aggiorna(dati.id, { titolo: dati.titolo, tipo: dati.tipo, contenuto });
            setVersione((v) => v + 1);
            setStatoSalva('salvato');
            setSalvatoQualcosa(true);
        } catch (e) {
            setStatoSalva('non_salvato');
            setErrore(`Non salvato: ${messaggio(e, 'il salvataggio non è riuscito')}. Il testo è ancora nel campo.`);
        } finally {
            setInCorso(false);
        }
    };
    const esci = (e) => {
        if (e && e.currentTarget && e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
        salvaEsistente();
    };

    const chiudi = () => {
        if (!chiedi()) return;
        if (salvatoQualcosa) onSalvato(null, 'Salvato.');
        else onChiudi();
    };

    return (
        <div className="tr-modulo" data-salva-da-solo>
            {errore && <p className="rm-messaggio rm-messaggio-errore">{errore}</p>}
            <div className="tr-modulo-riga">
                <label className="tr-campo tr-campo-titolo">
                    <span>Titolo</span>
                    <input value={dati.titolo} maxLength={160}
                           onChange={(e) => setDati({ ...dati, titolo: e.target.value })}
                           onBlur={esci}
                           placeholder="Es. Indicazioni dopo il filler" />
                </label>
                {!tipoFisso && <label className="tr-campo">
                    <span>Tipo</span>
                    <select value={dati.tipo} onChange={(e) => setDati({ ...dati, tipo: e.target.value })} onBlur={esci}>
                        {TIPI.map((t) => <option key={t.chiave} value={t.chiave}>{t.etichetta}</option>)}
                    </select>
                </label>}
            </div>
            <p className="tr-spiega">{TIPI.find((t) => t.chiave === dati.tipo)?.spiegazione}</p>
            <div onBlur={esci}>
                <EditorReferto
                    contenuto={dati.contenuto}
                    onCambia={(c) => setDati((d) => ({ ...d, contenuto: c }))}
                />
            </div>
            <div className="tr-modulo-azioni">
                {dati.id && statoSalva === 'in_corso' && <span className="tr-stato-salva">salvataggio…</span>}
                {dati.id && statoSalva === 'salvato' && !sporco && <span className="tr-stato-salva tr-stato-ok">✓ salvato</span>}
                {dati.id && statoSalva === 'non_salvato' && (
                    <button type="button" className="rm-btn" onClick={salvaEsistente} disabled={inCorso}>
                        <Save size={15} /> Riprova
                    </button>
                )}
                <button type="button" className="rm-btn" onClick={chiudi} disabled={inCorso}>
                    <X size={15} /> {dati.id ? 'Chiudi' : 'Annulla'}
                </button>
                {!dati.id && (
                    <button type="button" className="rm-btn rm-btn-primario" onClick={salva}
                            disabled={inCorso || !sporco || !dati.titolo.trim()}>
                        <Save size={15} /> {inCorso ? 'Creo…' : 'Crea il testo'}
                    </button>
                )}
            </div>
        </div>
    );
};

const TestiRiutilizzabili = ({ soloTipo = null }) => {
    const ricette = soloTipo === 'ricetta';
    const utente = useAuthStore((s) => s.user);
    const amministra = utente?.ruolo === 'admin';
    const collegato = Boolean(utente?.medico_id);

    const [testi, setTesti] = useState([]);
    const [vista, setVista] = useState('tutti');
    const [aperto, setAperto] = useState(null);      // null | 'nuovo' | testo
    const [avviso, setAvviso] = useState(null);
    const [errore, setErrore] = useState(null);
    const [cerca, setCerca] = useState('');

    const carica = useCallback(async () => {
        try {
            const r = await testiAPI.elenco({ ...(amministra ? { tutti: 1 } : {}), ...(soloTipo ? { tipo: soloTipo } : {}) });
            setTesti(r.data?.data?.testi || []);
            setErrore(null);
        } catch (e) {
            setErrore(messaggio(e, 'Non riesco a leggere la libreria'));
        }
    }, [amministra, soloTipo]);

    useEffect(() => { carica(); }, [carica]);

    const q = cerca.trim().toLowerCase();
    const visibili = testi.filter((t) => (soloTipo ? t.tipo === soloTipo : (vista === 'tutti' || t.tipo === vista))
        && (!q || String(t.titolo).toLowerCase().includes(q) || String(t.anteprima || '').toLowerCase().includes(q)));
    const struttura = visibili.filter((t) => t.portata === 'struttura');
    const miei = visibili.filter((t) => t.portata === 'mio' && Number(t.medico_id) === Number(utente?.medico_id));
    const altrui = visibili.filter((t) => t.portata === 'mio' && Number(t.medico_id) !== Number(utente?.medico_id));

    const puoScrivere = (t) => (t.portata === 'struttura'
        ? ['admin', 'segreteria'].includes(utente?.ruolo)
        : amministra || Number(t.medico_id) === Number(utente?.medico_id));

    const promuovi = async (t) => {
        if (!window.confirm(`Promuovere «${t.titolo}» a testo della struttura?\n\nLo vedranno e potranno usarlo tutti i medici; da quel momento lo cura l'amministrazione.`)) return;
        try {
            const r = await testiAPI.promuovi(t.id);
            setAvviso(r.data?.message);
            carica();
        } catch (e) { setErrore(messaggio(e, 'Errore nella promozione')); }
    };

    /* Ritirare una ricetta: si spegne, non si cancella. Le visite che l'hanno
       usata tengono la loro copia. */
    const ritira = async (t) => {
        if (!window.confirm(`Ritirare «${t.titolo}»?\n\nNon compare più fra le ricette da aggiungere. Le ricette già scritte nelle visite non cambiano: lì dentro il testo è una copia.`)) return;
        try {
            await testiAPI.aggiorna(t.id, { attivo: false });
            if (aperto?.id === t.id) setAperto(null);
            setAvviso('Ricetta ritirata.');
            carica();
        } catch (e) { setErrore(messaggio(e, 'Errore nel ritiro della ricetta')); }
    };

    const elimina = async (t) => {
        if (!window.confirm(`Eliminare «${t.titolo}»?\n\nI referti in cui è già stato inserito non cambiano: il testo lì dentro è una copia.`)) return;
        try {
            await testiAPI.cancella(t.id);
            if (aperto?.id === t.id) setAperto(null);
            carica();
        } catch (e) { setErrore(messaggio(e, 'Errore nell\'eliminazione')); }
    };

    const Elenco = ({ titolo, voci, nota, promuovibili = false }) => (
        <section className="tr-gruppo">
            <h2>{titolo} <span className="tr-conta">{voci.length}</span></h2>
            {nota && <p className="tr-nota">{nota}</p>}
            {voci.length === 0 ? <p className="tr-vuoto">{ricette ? 'Nessuna ricetta.' : 'Nessun testo.'}</p> : (
                <ul className="tr-elenco">
                    {voci.map((t) => (
                        <li key={t.id} className={`tr-voce ${t.attivo === false ? 'tr-disattivo' : ''}`}>
                            <div className="tr-voce-testa">
                                <span className="tr-tipo">{etichettaTipo(t.tipo)}</span>
                                <strong>{t.titolo}</strong>
                                {t.medico_cognome && <span className="tr-autore">{t.medico_cognome} {t.medico_nome}</span>}
                                {t.attivo === false && <span className="tr-tipo">disattivato</span>}
                            </div>
                            <p className="tr-anteprima">{t.anteprima}{t.anteprima?.length >= 160 ? '…' : ''}</p>
                            <div className="tr-voce-azioni">
                                {puoScrivere(t) && (
                                    <button type="button" className="rm-btn" onClick={() => setAperto(t)}>
                                        <Pencil size={14} /> Modifica
                                    </button>
                                )}
                                {promuovibili && amministra && (
                                    <button type="button" className="rm-btn" onClick={() => promuovi(t)}
                                            title="Da personale a condiviso con tutti i medici">
                                        <ArrowUpToLine size={14} /> Promuovi
                                    </button>
                                )}
                                {puoScrivere(t) && ricette && t.attivo !== false && (
                                    <button type="button" className="rm-btn rm-btn-pericolo" onClick={() => ritira(t)}>
                                        <Trash2 size={14} /> Ritira
                                    </button>
                                )}
                                {puoScrivere(t) && !ricette && (
                                    <button type="button" className="rm-btn rm-btn-pericolo" onClick={() => elimina(t)}>
                                        <Trash2 size={14} /> Elimina
                                    </button>
                                )}
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );

    const puoCreare = ['admin', 'segreteria', 'medico', 'medico_senior'].includes(utente?.ruolo);
    /* Le azioni della barra comandi (Ctrl K) e di «Nuovo»: lo stesso gesto
       del pulsante della pagina, alle stesse condizioni. Le ricette sono la
       stessa pagina col solo tipo «ricetta»: ognuna risponde alla sua. */
    useAzione({ [ricette ? 'nuova-ricetta' : 'nuovo-testo']: () => { if (puoCreare) setAperto('nuovo'); } });

    return (
        <div className="tr-pagina">
            {ricette ? (
                <IntestazioneSezione
                    titolo="Ricette"
                    icona={<FileText size={22} aria-hidden="true" />}
                    sottotitolo={collegato
                        ? 'Le ricette che scrivi una volta e aggiungi con un clic alla ricetta di una visita. Le tue le vedi solo tu; quelle della struttura le vedono tutti.'
                        : 'Le ricette della struttura, a disposizione di tutti i medici.'}
                    azione={puoCreare && (
                        <button type="button" className="rm-btn rm-btn-primario" onClick={() => setAperto('nuovo')}>
                            <Plus size={15} /> Nuova ricetta
                        </button>
                    )}
                />
            ) : (
                <IntestazioneSezione
                    titolo="Testi riutilizzabili"
                    icona={<BookOpen size={22} aria-hidden="true" />}
                    sottotitolo={collegato
                        ? 'Frasi e protocolli che scrivi una volta e inserisci nei referti. I tuoi li vedi solo tu; quelli della struttura li vedono tutti.'
                        : 'Frasi e protocolli della struttura, a disposizione di tutti i medici.'}
                    viste={[{ chiave: 'tutti', etichetta: 'Tutti', contatore: testi.length },
                            ...TIPI.map((t) => ({ chiave: t.chiave, etichetta: t.etichetta + (t.chiave === 'altro' ? '' : ''),
                                                  contatore: testi.filter((x) => x.tipo === t.chiave).length }))]}
                    vista={vista}
                    onVista={setVista}
                    azione={puoCreare && (
                        <button type="button" className="rm-btn rm-btn-primario" onClick={() => setAperto('nuovo')}>
                            <Plus size={15} /> Nuovo testo
                        </button>
                    )}
                />
            )}

            <label className="tr-cerca" data-non-trattiene>
                <Search size={15} aria-hidden="true" />
                <input value={cerca} onChange={(e) => setCerca(e.target.value)}
                       placeholder={ricette ? 'Cerca una ricetta…' : 'Cerca un testo…'}
                       aria-label={ricette ? 'Cerca una ricetta' : 'Cerca un testo'} />
            </label>

            {errore && <p className="rm-messaggio rm-messaggio-errore">{errore}</p>}
            {avviso && <p className="rm-messaggio rm-messaggio-conferma">{avviso}
                <button type="button" className="tr-chiudi-avviso" onClick={() => setAvviso(null)}><X size={14} /></button></p>}

            {aperto && (
                <ModuloTesto
                    iniziale={aperto === 'nuovo' ? (soloTipo ? { ...VUOTO, tipo: soloTipo } : null) : aperto}
                    tipoFisso={soloTipo}
                    onSalvato={(t, msg) => { setAperto(null); setAvviso(msg || 'Salvato.'); carica(); }}
                    onChiudi={() => setAperto(null)}
                />
            )}

            <Elenco titolo="Della struttura" voci={struttura}
                    nota={amministra ? 'Condivisi con tutti i medici. Li cura l\'amministrazione.' : null} />
            {collegato && (
                <Elenco titolo="I miei" voci={miei}
                        nota="Li vedi solo tu. Se vuoi che diventino della struttura, chiedi all'amministrazione di promuoverli." />
            )}
            {amministra && (
                <Elenco titolo="Personali dei medici" voci={altrui} promuovibili
                        nota="Scritti dai medici per sé. «Promuovi» li rende della struttura: da quel momento li vedono tutti e li cura l'amministrazione." />
            )}
        </div>
    );
};

export default TestiRiutilizzabili;
